import { useCallback, useMemo } from 'react'

import { Bot } from 'lucide-react'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_CALL_ACTIVITY_BY_EMPLOYEE } from 'src/graphql/callAnaltyics.graphql'
import AnalyticsCard from 'src/pages/AnalyticsPage/components/AnalyticsCard'
import {
  LeaderboardCardView,
  type LeaderboardColumn,
} from 'src/pages/AnalyticsPage/components/LeaderboardCardView'
import { formatDuration } from 'src/pages/AnalyticsPage/components/heroStatHelpers'
import { useRefetchOnKeyChange } from 'src/pages/AnalyticsPage/components/trendChartHelpers'

import type { CallLogFiltersInput } from 'types/graphql'

interface CallActivityByEmployeeRowShape {
  rowId: string
  rowName: string
  isVoiceAgent: boolean
  completed: number
  noAnswer: number
  busy: number
  leftVoicemail: number
  failed: number
  total: number
  avgDurationSeconds: number | null
  totalDurationSeconds: number
}

// Shape required by LeaderboardCardView<T extends { id: string }>: `id` is
// used as the React key. We adapt rowId → id at the boundary.
interface TableRow extends CallActivityByEmployeeRowShape {
  id: string
}

const INFO_TEXT =
  'One row per employee and voice agent with calls in the filtered window. Click any column header to re-sort — default is total calls (descending). Avg Duration counts CONNECTED calls only; Total Time sums every attempt.'

const renderName = (row: TableRow) => (
  <div className="flex items-center gap-1.5">
    {row.isVoiceAgent && (
      <Bot
        size={14}
        className="text-doubleNickel-brand-500"
        aria-label="Voice agent"
      />
    )}
    <span className={row.isVoiceAgent ? 'font-medium' : ''}>{row.rowName}</span>
  </div>
)

const renderCount = (n: number) =>
  n === 0 ? <span className="text-gray-400">0</span> : n.toLocaleString()

export interface CallActivityByEmployeeContainerProps {
  filters: CallLogFiltersInput
  refreshKey: number
}

export const CallActivityByEmployeeContainer = ({
  filters,
  refreshKey,
}: CallActivityByEmployeeContainerProps) => {
  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_CALL_ACTIVITY_BY_EMPLOYEE, {
    variables: { filters },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  useRefetchOnKeyChange(refetch, refreshKey)

  const rows = useMemo<ReadonlyArray<TableRow>>(() => {
    const items =
      (data?.callActivityByEmployee?.items as
        | ReadonlyArray<CallActivityByEmployeeRowShape>
        | undefined) ?? []
    return items.map((row) => ({ ...row, id: row.rowId }))
  }, [data])

  const columns: ReadonlyArray<LeaderboardColumn<TableRow>> = [
    {
      key: 'rowName',
      label: 'Employee',
      align: 'left',
      render: renderName,
      // Sortable by name (alphabetical) is useful when the user is
      // looking for a specific person rather than ranking by activity.
      sortable: true,
    },
    {
      key: 'completed',
      label: 'Completed',
      render: (r) => renderCount(r.completed),
    },
    {
      key: 'noAnswer',
      label: 'No Answer',
      render: (r) => renderCount(r.noAnswer),
    },
    { key: 'busy', label: 'Busy', render: (r) => renderCount(r.busy) },
    {
      key: 'leftVoicemail',
      label: 'Left VM',
      render: (r) => renderCount(r.leftVoicemail),
    },
    { key: 'failed', label: 'Failed', render: (r) => renderCount(r.failed) },
    {
      key: 'total',
      label: 'Total',
      render: (r) => (
        <span className="font-medium">{renderCount(r.total)}</span>
      ),
    },
    {
      key: 'avgDurationSeconds',
      label: 'Avg Duration',
      render: (r) => formatDuration(r.avgDurationSeconds),
      tooltip:
        'Average length of CONNECTED calls for this row. "—" when there are no connected calls.',
    },
    {
      key: 'totalDurationSeconds',
      label: 'Total Time',
      render: (r) => formatDuration(r.totalDurationSeconds),
      tooltip: 'Sum of every call attempt — connected, missed, busy, etc.',
    },
  ]

  // Pre-aggregated totals row. Sums map cleanly across counts; avg duration
  // is the weighted mean (sum of all totalDurationSeconds for completed
  // calls ÷ sum of completed counts) — but since we don't have per-row
  // connected-only duration, we use the grand-total / completed-total as a
  // close-enough approximation, and pre-format it.
  const totals = useMemo(() => {
    if (rows.length === 0) return undefined
    const summed = rows.reduce(
      (acc, r) => ({
        completed: acc.completed + r.completed,
        noAnswer: acc.noAnswer + r.noAnswer,
        busy: acc.busy + r.busy,
        leftVoicemail: acc.leftVoicemail + r.leftVoicemail,
        failed: acc.failed + r.failed,
        total: acc.total + r.total,
        totalDurationSeconds: acc.totalDurationSeconds + r.totalDurationSeconds,
      }),
      {
        completed: 0,
        noAnswer: 0,
        busy: 0,
        leftVoicemail: 0,
        failed: 0,
        total: 0,
        totalDurationSeconds: 0,
      }
    )
    return {
      completed: summed.completed.toLocaleString(),
      noAnswer: summed.noAnswer.toLocaleString(),
      busy: summed.busy.toLocaleString(),
      leftVoicemail: summed.leftVoicemail.toLocaleString(),
      failed: summed.failed.toLocaleString(),
      total: summed.total.toLocaleString(),
      // Average across rows isn't well-defined here; omit so the totals
      // cell renders blank ("—").
      avgDurationSeconds: '—',
      totalDurationSeconds: formatDuration(summed.totalDurationSeconds),
    }
  }, [rows])

  return (
    <AnalyticsCard title="Call Activity" info={INFO_TEXT}>
      <LeaderboardCardView<TableRow>
        columns={columns}
        rows={rows}
        loading={loading}
        emptyMessage="No calls in this period"
        defaultSortKey="total"
        defaultSortDir="desc"
        totals={totals}
      />
    </AnalyticsCard>
  )
}

export default CallActivityByEmployeeContainer
